<template>
	<div :class="groupClass" role="radiogroup">
		<slot></slot>
	</div>
</template>

<script>
/**
 * BFormRadioGroup Compatibility Wrapper
 * 
 * CREATED: Dec 2024 - Vue 3 Migration
 * WHO: GitHub Copilot (Claude Sonnet 4.5)
 * WHY: BootstrapVue 2.23.1 incompatible with Vue 3; provides bridge component
 * 
 * PURPOSE:
 * - Drop-in replacement for BootstrapVue's b-form-radio-group
 * - Manages state for child b-form-radio components
 * - Mimics BootstrapVue API for minimal migration effort
 * 
 * USAGE:
 * <b-form-radio-group v-model="selected" name="myRadios">
 *   <b-form-radio value="option1">Option 1</b-form-radio>
 *   <b-form-radio value="option2">Option 2</b-form-radio>
 * </b-form-radio-group>
 */

export default {
	name: 'BFormRadioGroup',
	props: {
		// Vue 3 uses modelValue instead of value
		modelValue: {
			type: [String, Number, Boolean, Object],
			default: null
		},
		name: {
			type: String,
			required: true
		},
		id: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		state: {
			type: Boolean,
			default: null
		},
		size: {
			type: String,
			default: null,
			validator: (value) => ['sm', 'lg', null].includes(value)
		},
		buttons: {
			type: Boolean,
			default: false
		},
		buttonVariant: {
			type: String,
			default: 'secondary'
		},
		stacked: {
			type: Boolean,
			default: false
		}
	},
	
	computed: {
		groupClass() {
			const classes = []
			
			if (this.buttons) {
				classes.push('btn-group')
				if (this.size) classes.push(`btn-group-${this.size}`)
			}
			
			if (this.stacked && !this.buttons) {
				classes.push('d-flex', 'flex-column')
			}
			
			return classes
		}
	},
	
	provide() {
		return {
			radioGroupName: this.name,
			radioGroupValue: this.modelValue,
			radioGroupDisabled: this.disabled,
			radioGroupRequired: this.required,
			radioGroupInline: !this.stacked,
			updateRadioGroup: this.updateValue
		}
	},
	
	methods: {
		updateValue(value) {
			this.$emit('update:modelValue', value)
			this.$emit('change', value)
			this.$emit('input', value)
		}
	},
	
	watch: {
		modelValue: {
			handler() {
				// Provide updated value to children
				this.$forceUpdate()
			}
		}
	}
}
</script>

<style scoped>
.btn-group {
	position: relative;
	display: inline-flex;
	vertical-align: middle;
}

.btn-group-sm {
	font-size: 0.875rem;
}

.btn-group-lg {
	font-size: 1.25rem;
}
</style>
